import React from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/common/SEO';
import { FaArrowRight } from 'react-icons/fa';
import theme from '../../theme';

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  padding: 120px 5% 70px;
  background: white;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 2.75rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: ${theme.breakpoints.tablet}) { font-size: 2rem; }
  @media (max-width: ${theme.breakpoints.mobile}) { font-size: 1.6rem; }
`;

const HeroText = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.darkGray};
  line-height: 1.8;
  max-width: 620px;
  margin: 0 auto;
`;

const SectionBlock = styled.section`
  padding: 60px 5%;
  background: ${props => props.$gray ? '#F7F7F9' : 'white'};
`;

const SectionContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const SectionLabel = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${theme.colors.darkGray};
  margin-bottom: 0.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 1.25rem;
  line-height: 1.3;
`;

const SectionText = styled.p`
  font-size: 0.95rem;
  color: ${theme.colors.darkGray};
  line-height: 1.8;
  margin-bottom: 1rem;

  &:last-child { margin-bottom: 0; }
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.25rem 0 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BulletItem = styled.li`
  font-size: 0.95rem;
  color: ${theme.colors.secondary};
  line-height: 1.5;
  padding-left: 1.25rem;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.55rem;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${theme.colors.secondary};
  }
`;

const PrinciplesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  max-width: 720px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PrincipleCard = styled.div`
  h3 {
    font-size: 1rem;
    font-weight: 700;
    color: ${theme.colors.secondary};
    margin-bottom: 0.4rem;
  }

  p {
    font-size: 0.88rem;
    color: ${theme.colors.darkGray};
    line-height: 1.65;
  }
`;

const ClosingSection = styled.section`
  padding: 60px 5%;
  background: white;
  text-align: center;
`;

const ClosingText = styled.p`
  font-size: 1.05rem;
  font-weight: 600;
  color: ${theme.colors.secondary};
  margin-bottom: 2rem;
  font-style: italic;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2.25rem;
  background: #8B0000;
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(139, 0, 0, 0.3);

  &:hover {
    background: #6b0000;
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(139, 0, 0, 0.45);
    color: white;
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
  }
  &:hover svg { transform: translateX(4px); }
`;

const MissionVisionPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const d = (key) => t(`missionVision.${key}`);

  return (
    <>
      <SEO titleKey="missionVision.seo.title" descriptionKey="missionVision.seo.description" />

      <PageContainer>
        <HeroSection>
          <HeroContent>
            <HeroTitle>{d('hero.title')}</HeroTitle>
            <HeroText>{d('hero.text1')}</HeroText>
            <HeroText style={{ marginTop: '0.75rem' }}>{d('hero.text2')}</HeroText>
          </HeroContent>
        </HeroSection>

        <SectionBlock>
          <SectionContainer>
            <SectionLabel>{d('mission.label')}</SectionLabel>
            <SectionTitle>{d('mission.title')}</SectionTitle>
            <SectionText>{d('mission.text')}</SectionText>
            <SectionText>{d('mission.focusIntro')}</SectionText>
            <BulletList>
              <BulletItem>{d('mission.b1')}</BulletItem>
              <BulletItem>{d('mission.b2')}</BulletItem>
              <BulletItem>{d('mission.b3')}</BulletItem>
              <BulletItem>{d('mission.b4')}</BulletItem>
            </BulletList>
            <SectionText>{d('mission.closing')}</SectionText>
          </SectionContainer>
        </SectionBlock>

        <SectionBlock $gray>
          <SectionContainer>
            <SectionLabel>{d('vision.label')}</SectionLabel>
            <SectionTitle>{d('vision.title')}</SectionTitle>
            <SectionText>{d('vision.text')}</SectionText>
            <SectionText>{d('vision.aimIntro')}</SectionText>
            <SectionText>{d('vision.growthIntro')}</SectionText>
            <BulletList>
              <BulletItem>{d('vision.b1')}</BulletItem>
              <BulletItem>{d('vision.b2')}</BulletItem>
              <BulletItem>{d('vision.b3')}</BulletItem>
            </BulletList>
          </SectionContainer>
        </SectionBlock>

        <SectionBlock>
          <SectionContainer>
            <SectionLabel>{d('principles.label')}</SectionLabel>
            <PrinciplesGrid>
              <PrincipleCard>
                <h3>{d('principles.p1.title')}</h3>
                <p>{d('principles.p1.text')}</p>
              </PrincipleCard>
              <PrincipleCard>
                <h3>{d('principles.p2.title')}</h3>
                <p>{d('principles.p2.text')}</p>
              </PrincipleCard>
              <PrincipleCard>
                <h3>{d('principles.p3.title')}</h3>
                <p>{d('principles.p3.text')}</p>
              </PrincipleCard>
              <PrincipleCard>
                <h3>{d('principles.p4.title')}</h3>
                <p>{d('principles.p4.text')}</p>
              </PrincipleCard>
            </PrinciplesGrid>
          </SectionContainer>
        </SectionBlock>

        <ClosingSection>
          <ClosingText>{d('closing')}</ClosingText>
          <CTAButton to={`/${lang}/demo`}>
            {d('cta.button')} <FaArrowRight />
          </CTAButton>
        </ClosingSection>
      </PageContainer>
    </>
  );
};

export default MissionVisionPage;
